<template>
  <button
    :id="`${type}_type_field_button`"
    draggable="true"
    class="field-type-button group flex items-center border w-full rounded relative fields-grid-item"
    :style="{ backgroundColor }"
    :class="armed ? 'border-solid border-primary ring-2 ring-primary/30' : 'border-dashed border-base-300 hover:border-base-content/20'"
    @dragstart="onDragstart"
    @drag="onDrag"
    @dragend="onDragend"
    @click="$emit('click', $event)"
  >
    <div
      class="flex items-center transition-all cursor-grab h-full absolute left-0"
      :class="armed ? 'bg-base-200/50' : 'group-hover:bg-base-200/50'"
    >
      <IconDrag class="my-auto" />
    </div>
    <!-- One row per type, icon left of label, the way DocuSign's field palette reads. pl-5
         clears the absolutely-positioned drag handle above; the hand-rolled rows in fields.vue
         (the verification lock and the smart fields) repeat that padding so every label in the
         list starts at the same x, handle or no handle. -->
    <div class="flex items-center gap-2 pl-5 pr-2 py-1.5">
      <component :is="icon" />
      <span class="text-sm">
        {{ label }}
      </span>
    </div>
  </button>
</template>

<script>
import IconDrag from './icon_drag'

// Browsers commit to a native drag once the pointer has moved roughly 4-5px past mousedown, so
// anything under that threshold was never a real drag -- it's a click the browser reclassified.
// Shared by onDrag (which latches the "moved" flag as the drag is happening) and onDragend
// (which used to re-measure this from the two endpoints alone -- see onDrag for why that isn't
// enough).
const CLICK_DISTANCE_THRESHOLD = 5

export default {
  name: 'FieldTypeButton',
  components: {
    IconDrag
  },
  props: {
    type: {
      type: String,
      required: true
    },
    icon: {
      type: [Object, Function],
      required: true
    },
    label: {
      type: String,
      required: true
    },
    armed: {
      type: Boolean,
      required: false,
      default: false
    },
    backgroundColor: {
      type: String,
      required: false,
      default: ''
    }
  },
  emits: ['click', 'dragstart', 'dragend'],
  data () {
    return {
      // Set on dragstart, read (and cleared) on dragend. A palette button is draggable so it
      // can be dragged onto the document, but that means the browser reclassifies a click as a
      // native drag the moment the pointer drifts a few pixels between mousedown and mouseup --
      // and once it does, the click event never fires at all. dragStartPoint anchors the
      // distance check; see onDrag/onDragend below for why the check has to look at the whole
      // path and not just start-vs-end.
      //
      // Owned here rather than by a parent: it's this button's own draggable-vs-clickable
      // ambiguity to resolve, not the palette's, so both the sidebar grid and the toolbar get
      // the fix for free just by rendering this component -- neither parent has to know about it.
      dragStartPoint: null,
      // Latched true the first time onDrag sees the pointer more than CLICK_DISTANCE_THRESHOLD
      // from dragStartPoint. Endpoint-only measurement (comparing dragstart's point to
      // dragend's release point) can't tell "the browser stole my click" apart from "I dragged
      // away and released back near where I grabbed" -- both produce a small start-to-end
      // distance. Sampling the whole path during the drag is what tells them apart.
      dragMoved: false
    }
  },
  methods: {
    onDragstart (event) {
      this.dragStartPoint = { x: event.clientX, y: event.clientY }
      this.dragMoved = false

      this.$emit('dragstart', event)
    },
    // Fires continuously while the drag is in progress. Browsers emit a { clientX: 0, clientY:
    // 0 } sample when the pointer is outside the viewport during a drag -- that's a sentinel,
    // not a real position, and treating it as one would compute a huge distance, latch
    // dragMoved on every drag regardless of how far the pointer actually travelled, and
    // reopen the original intermittent bug (a jittery click failing to arm).
    onDrag (event) {
      if (event.clientX === 0 && event.clientY === 0) return

      if (!this.dragStartPoint || this.dragMoved) return

      const distance = Math.hypot(event.clientX - this.dragStartPoint.x, event.clientY - this.dragStartPoint.y)

      if (distance >= CLICK_DISTANCE_THRESHOLD) {
        this.dragMoved = true
      }
    },
    // dropEffect can't tell a browser-stolen click apart from a genuine abandoned drag (and is
    // unreliable in Firefox -- Mozilla #1548949), so path distance is the signal: a drag whose
    // pointer never travelled past CLICK_DISTANCE_THRESHOLD (per onDrag above) was never a real
    // drag, it's a click the browser reclassified.
    //
    // A genuine drag-and-drop is also observed to place its field via the drop handler before
    // dragend fires here, but that ordering is not what stops this from double-adding a field --
    // it's the dragMoved gate: onDrag already latched true well before release, so the branch
    // below is never taken for a real drag regardless of event ordering.
    //
    // The synthesized click is flagged `recovered: true` so callers that would otherwise call
    // addField with no area (file/payment/verification -- a required field with an unplaced
    // area) can treat a recovered click as a no-op instead. See fields.vue and builder.vue.
    onDragend (event) {
      const start = this.dragStartPoint
      const moved = this.dragMoved

      this.dragStartPoint = null
      this.dragMoved = false

      this.$emit('dragend', event)

      if (start && !moved) {
        this.$emit('click', event, { recovered: true })
      }
    }
  }
}
</script>
